import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui/sheet";
import type { FlatSection } from "./types";
import { FlatSectionDispatch } from "./FlatSectionDispatch";

interface RowDetailPayload {
  title: string;
  subtitle?: string;
  sections: FlatSection[];
}

interface RowDetailContextValue {
  open: (payload: RowDetailPayload) => void;
  close: () => void;
}

const RowDetailContext = createContext<RowDetailContextValue | null>(null);

/**
 * Provides a single shared Sheet for table row detail drill-downs. Any
 * SectionTable can call useRowDetail().open({...}) and the same Sheet flips
 * open with the nested sections rendered inside.
 */
export function RowDetailProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<RowDetailPayload | null>(null);
  const open = useCallback((p: RowDetailPayload) => setPayload(p), []);
  const close = useCallback(() => setPayload(null), []);

  return (
    <RowDetailContext.Provider value={{ open, close }}>
      {children}
      <Sheet
        open={payload !== null}
        onOpenChange={(o) => {
          if (!o) close();
        }}
      >
        <SheetContent side="right" className="overflow-y-auto p-0 sm:max-w-xl">
          <SheetHeader className="px-6 pt-6">
            <SheetTitle className="text-xl font-semibold tracking-tight">
              {payload?.title ?? "Detail"}
            </SheetTitle>
            {payload?.subtitle ? (
              <SheetDescription>{payload.subtitle}</SheetDescription>
            ) : null}
          </SheetHeader>
          {payload ? (
            <div className="px-6 pb-8 pt-3 space-y-4">
              {(payload.sections ?? []).map((s, i) => (
                <FlatSectionDispatch key={i} section={s} />
              ))}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </RowDetailContext.Provider>
  );
}

export function useRowDetail() {
  const ctx = useContext(RowDetailContext);
  if (!ctx) throw new Error("useRowDetail must be inside <RowDetailProvider>");
  return ctx;
}
